import React from 'react';

interface Styles {
    [key: string]: string;
}

function compose<S extends {}>(styles: {}, previousStyles: S) {
    const composed: Styles = { ...previousStyles };

    const localsMap: Map<string, string> = new Map();

    Object.keys(previousStyles).forEach(key => {
        localsMap.set((previousStyles as Styles)[key].split(' ').shift()!, key);
    });

    Object.keys(styles).forEach(key => {
        const classNames = (styles as Styles)[key].split(' ');

        for (let i = 0; i < classNames.length; i++) {
            // tslint:disable-line
            const className = classNames[i];

            const localKey = localsMap.get(className);

            if (localKey) {
                composed[localKey] = (styles as Styles)[key];
                break;
            }
        }
    });

    return composed as S;
}

type InferProps<T> = T extends React.ComponentType<infer P> | React.Component<infer P> ? P : never;

type StyledComponent<P = any, S = any> = React.ComponentType<P> & {
    extend: (styles: {} | undefined | null) => StyledComponent<P, S>;
    compose: (styles: {} | undefined | null) => S;
};

const Styled = <S extends {}>(styles: S) => <C extends React.ComponentType<P>, P extends { styles: S } = any>(
    Component: C
) => {
    type InferedProps = InferProps<C>;
    type DefaultProps = Pick<P, keyof typeof Component['defaultProps']>;
    type Props = Pick<InferedProps, Exclude<keyof InferedProps, keyof DefaultProps | 'styles'>> &
        { [K in keyof DefaultProps]+?: DefaultProps[K] } & { styles?: S };

    Component.defaultProps = {
        ...Component.defaultProps,
        styles,
    } as Partial<P>;

    (Component as any).extend = (newStyles: {}) => {
        const Extended = (props: Props) => React.createElement(Component, props as InferedProps);
        Extended.displayName = `Extended${Component.displayName || (Component as any).name}`;

        return Styled(compose(newStyles, styles))(Extended);
    };

    (Component as any).compose = (newStyles: {} | undefined | null) =>
        newStyles ? compose(newStyles, styles) : (styles as S);

    return (Component as any) as StyledComponent<Props, S>;
};

export default Styled;
